import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import Hummingbird from "../brand/Hummingbird";
import NetworkGlobe from "../NetworkGlobe";

const API = process.env.REACT_APP_BACKEND_URL;

const Hero = () => {
  const { t } = useLang();
  const stats = t("hero.stats");
  const [liveCount, setLiveCount] = useState(null);
  const [pulseTick, setPulseTick] = useState(0);
  const [lastSeenId, setLastSeenId] = useState(null);
  const lastSeenRef = useRef(null);

  // ── Fetch live community count (refreshed every minute) ────────────
  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const r = await axios.get(`${API}/api/herz/stats`);
        if (cancelled) return;
        // Brand baseline (SAT begleitete Menschen) + live community activity
        const acts = r.data?.total_acts || 0;
        const helped = r.data?.people_helped || 0;
        const members = r.data?.active_members || 0;
        const BRAND_BASELINE = 1200;
        setLiveCount(BRAND_BASELINE + acts + helped + members);
      } catch (_) { /* silent */ }
    };
    fetchStats();
    const iv = setInterval(fetchStats, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // ── Poll latest heart-act every 25s, spawn gold pulse on the globe ─
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/herz/feed?limit=1`);
        if (cancelled) return;
        const latest = r.data?.[0];
        if (!latest) return;
        if (lastSeenRef.current && latest.id !== lastSeenRef.current) {
          setPulseTick((p) => p + 1);
        }
        lastSeenRef.current = latest.id;
        setLastSeenId(latest.id);
      } catch (_) { /* silent */ }
    };
    poll();
    const iv = setInterval(poll, 25000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Format count: 1200 → "1'200+", small numbers stay raw
  const formatCount = (n) => {
    if (n == null) return null;
    if (n < 50) return `${n}`;
    if (n < 1000) return `${Math.floor(n / 10) * 10}+`;
    return `${n.toLocaleString("de-CH")}+`;
  };

  return (
    <section className="relative min-h-[100svh] flex items-center pt-32 pb-20 overflow-hidden" data-testid="hero-section">
      {/* Cinematic background video with Ken-Burns fallback */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <video
          className="w-full h-full object-cover opacity-[0.45] lg:opacity-[0.35]"
          autoPlay
          loop
          muted
          playsInline
          poster="https://images.unsplash.com/photo-1678247539185-aac2092e057f?crop=entropy&cs=srgb&fm=jpg&q=90&w=2600"
          data-testid="hero-video"
        >
          <source src="https://videos.pexels.com/video-files/3129957/3129957-uhd_2560_1440_25fps.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#081328]/55 via-[#081328]/55 to-[#081328]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#081328] via-transparent to-transparent opacity-80"></div>
      </div>

      <div className="container-sat relative z-20 w-full">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 xl:gap-16 items-center">
          {/* ─── Left column: copy + CTAs + stats ──────────────────────────── */}
          <div className="max-w-2xl">
            <div className="reveal flex items-center gap-3 mb-8 lg:mb-10" style={{ animationDelay: "0.1s" }}>
              <span className="w-12 h-px bg-[#D4AF37]"></span>
              <span className="overline" data-testid="hero-kicker">{t("hero.kicker")}</span>
            </div>

            {/* All-caps Playfair headline — fluid scaling, doesn't blow up on 1920px */}
            <h1 className="headline-caps text-[11vw] sm:text-[8.5vw] lg:text-[5.2rem] xl:text-[6rem] 2xl:text-[6.6rem] reveal" data-testid="hero-title" style={{ animationDelay: "0.2s" }}>
              <span className="block text-[#F7F6F2]">DIE</span>
              <span className="block headline-italic" style={{ fontStyle: "italic", textTransform: "none", letterSpacing: "-0.01em", fontWeight: 500 }}>Spielregeln</span>
              <span className="block text-[#F7F6F2] whitespace-nowrap">DES LEBENS.</span>
            </h1>

            <p className="serif italic text-lg md:text-2xl text-[#F2D6D6]/65 mt-8 lg:mt-10 max-w-xl reveal" style={{ animationDelay: "0.35s" }}>
              „{t("hero.whisper")}&ldquo;
            </p>

            <div className="mt-7 lg:mt-8 space-y-1 reveal" style={{ animationDelay: "0.45s" }} data-testid="hero-subtitle">
              <p className="text-base md:text-xl text-[#F7F6F2]/85">{t("hero.subtitle_l1")}</p>
              <p className="text-base md:text-xl text-[#F7F6F2]/85">{t("hero.subtitle_l2")}</p>
              <p className="text-base md:text-xl text-[#F7F6F2]/85">{t("hero.subtitle_l3")}</p>
            </div>

            <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row gap-4 reveal" style={{ animationDelay: "0.55s" }}>
              <Link to="/erstgespraech" className="btn-primary pulse-gold" data-testid="hero-cta-primary">
                Kostenloses Erstgespräch buchen <ArrowRight size={16} />
              </Link>
              <Link to="/sat-htc" className="btn-secondary" data-testid="hero-cta-secondary">
                <Calendar size={16} /> SAT-HTC Methode entdecken
              </Link>
            </div>

            <div className="mt-14 lg:mt-16 grid grid-cols-3 max-w-2xl gap-5 lg:gap-8 reveal" style={{ animationDelay: "0.7s" }} data-testid="hero-stats">
              {stats.map((s, i) => (
                <div key={i} className="border-l border-[#D4AF37]/30 pl-4 lg:pl-5">
                  <div className="serif text-3xl md:text-4xl lg:text-[2.6rem] font-normal text-[#F7F6F2]">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#F7F6F2]/55 mt-2 leading-tight">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Right column: NetworkGlobe + Hummingbird (Desktop only) ───── */}
          <div className="hidden lg:flex relative items-center justify-center min-h-[640px] reveal" style={{ animationDelay: "0.4s" }}>
            {/* Soft violet glow behind globe */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[520px] h-[520px] xl:w-[600px] xl:h-[600px] rounded-full"
                   style={{ background: "radial-gradient(circle, rgba(75,46,131,0.30) 0%, rgba(75,46,131,0) 65%)" }} />
            </div>
            <div className="relative">
              <NetworkGlobe size={560} className="xl:!w-[640px] xl:!h-[640px]" pulseTick={pulseTick} />
              {/* Hummingbird floating just above-right of globe */}
              <div className="absolute -top-6 -right-6 xl:-top-8 xl:-right-10 hummingbird-float pointer-events-none" data-testid="hero-hummingbird">
                <Hummingbird size={200} />
              </div>
              {/* Caption beneath globe — live data */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]/70" data-testid="hero-live-caption">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF37] mr-2 align-middle animate-pulse"></span>
                {liveCount != null
                  ? <>{formatCount(liveCount)} Menschen · live verbunden</>
                  : <>Menschen weltweit · live verbunden</>}
              </div>
            </div>
          </div>

          {/* ─── Mobile/Tablet hummingbird (replaces globe) ─────────────────── */}
          <div className="lg:hidden absolute right-[4%] top-[22%] hummingbird-float opacity-90 pointer-events-none z-0">
            <Hummingbird size={150} />
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-6 lg:left-12 flex items-center gap-3 text-[10px] text-[#F7F6F2]/40 tracking-[0.3em] uppercase z-20">
        <span className="w-12 h-px bg-[#F7F6F2]/20"></span> scroll
      </div>
    </section>
  );
};

export default Hero;
